import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BackLink, Button, CardHeader, ErrorNotice, Field, LoadingState, PageEyebrow, Panel, TextInput } from '@bcecloud/portal-ui';
import { RichTextEditor } from '@/components/RichTextEditor';
import { errorMessage } from '@/lib/api';
import { previewMaterialsShippedEmail, readEvent, readEventShipping, sendMaterialsShippedEmail } from '@/lib/events';

/**
 * The "your program materials have shipped" email, as a page: who it goes
 * to on the left, the email itself on the right, editable before it goes.
 * The draft is the renderer's email for this event (receiving profile,
 * shipments, tracking links); the program manager can change any of it.
 */
export function MaterialsEmailPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const event = useQuery({ queryKey: ['event', eventId], queryFn: ({ signal }) => readEvent(eventId, signal) });
  const shipping = useQuery({ queryKey: ['event-shipping', eventId], queryFn: ({ signal }) => readEventShipping(eventId, signal) });
  const draft = useQuery({
    queryKey: ['materials-email-preview', eventId],
    queryFn: ({ signal }) => previewMaterialsShippedEmail(eventId, '', signal),
    staleTime: Infinity,
  });

  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [seeded, setSeeded] = useState(false);
  const [extra, setExtra] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [drafted, setDrafted] = useState(false);

  const contacts = (event.data?.contacts ?? []).filter((contact) => contact.email);
  if (!seeded && event.data) {
    // The primary contact starts ticked; everyone else is a click away.
    setChosen(new Set(contacts.filter((contact) => contact.isPrimary).map((contact) => contact.email as string)));
    setSeeded(true);
  }
  if (!drafted && draft.data) {
    setSubject(draft.data.subject);
    setHtml(draft.data.html);
    setDrafted(true);
  }
  const extraEmails = extra.split(/[,;\s]+/).map((value) => value.trim()).filter((value) => value.includes('@'));
  const to = [...new Set([...chosen, ...extraEmails])];
  const lastSent = shipping.data?.profile.clientEmails?.at(-1);
  const backTo = `/events/${eventId}?tab=shipments`;

  const send = useMutation({
    mutationFn: () => sendMaterialsShippedEmail(eventId, { to, subject: subject.trim(), html }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['event-shipping', eventId] });
      toast.success(`Emailed ${result.to.length === 1 ? result.to[0] : `${result.to.length} people`}`);
      navigate(backTo);
    },
  });
  const canSend = to.length > 0 && subject.trim().length > 0 && html.trim().length > 0 && !send.isPending;

  const title = event.data ? [event.data.contractNumber, event.data.account?.name].filter(Boolean).join(' · ') : 'Back to the event';

  return (
    <section className="animate-portal-rise">
      <BackLink to={backTo}>{title}</BackLink>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageEyebrow>CLIENT EMAIL</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1.7px]">Materials have shipped</h1>
          <p className="mt-1 text-sm text-admin-muted">
            Drafted from the receiving profile and this event's shipments, sent from you with replies coming back to you.
            {lastSent?.sentAt ? ` Last sent ${new Date(lastSent.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to ${(lastSent.to ?? []).join(', ')}.` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate(backTo)} disabled={send.isPending}>
            Cancel
          </Button>
          <Button onClick={() => send.mutate()} disabled={!canSend}>
            {send.isPending ? 'Sending…' : `Send${to.length > 1 ? ` to ${to.length}` : ''}`}
          </Button>
        </div>
      </header>

      {send.isError ? <div className="mt-4"><ErrorNotice inline title="Could not send" message={errorMessage(send.error)} /></div> : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <Panel className="p-5">
          <CardHeader title="To" description="The event's contacts with an email, plus anyone else." />
          {event.isPending ? (
            <LoadingState label="Loading contacts…" minimal />
          ) : contacts.length === 0 ? (
            <p className="text-portal-small text-portal-muted">No contacts with an email on this event; add one below.</p>
          ) : (
            <div className="space-y-1.5">
              {contacts.map((contact) => (
                <label key={contact.email} className="flex items-center gap-2 text-portal-small">
                  <input
                    type="checkbox"
                    checked={chosen.has(contact.email as string)}
                    onChange={(changeEvent) =>
                      setChosen((current) => {
                        const next = new Set(current);
                        if (changeEvent.target.checked) next.add(contact.email as string);
                        else next.delete(contact.email as string);
                        return next;
                      })
                    }
                  />
                  <span>
                    {contact.name || contact.email}
                    <span className="text-portal-muted"> · {contact.email}{contact.role ? ` · ${contact.role}` : ''}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          <div className="mt-4">
            <Field label="Also send to" htmlFor="materials-extra" hint="Comma separated.">
              <TextInput id="materials-extra" value={extra} placeholder="name@client.com" onChange={(changeEvent) => setExtra(changeEvent.target.value)} />
            </Field>
          </div>
          <p className="mt-3 text-portal-micro text-portal-muted">{to.length === 0 ? 'Pick at least one recipient.' : `${to.length} recipient${to.length === 1 ? '' : 's'}.`}</p>
        </Panel>

        <Panel className="p-5">
          <CardHeader title="The email" description="Change anything before it goes. Tracking links stay clickable." />
          {draft.isPending ? (
            <LoadingState label="Drafting the email…" minimal />
          ) : draft.isError ? (
            <ErrorNotice inline title="Could not draft the email" message={errorMessage(draft.error)} />
          ) : (
            <div className="space-y-4">
              <Field label="Subject" htmlFor="materials-subject" required>
                <TextInput id="materials-subject" value={subject} onChange={(changeEvent) => setSubject(changeEvent.target.value)} />
              </Field>
              <div>
                <span className="mb-1.5 block text-portal-tiny font-bold">Body</span>
                <RichTextEditor initialHtml={draft.data.html} onChange={setHtml} ariaLabel="Email body" />
              </div>
            </div>
          )}
        </Panel>
      </div>
    </section>
  );
}
